<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="token-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs" />
      <Card tag="form" @submit.prevent="submit" class="token-mutate__form">
        <template #default>
          <div class="token-mutate__inputs">
            <InputComponent
              v-model="data.name"
              :label="labels.name"
              required
              :is-valid="validator.fields.name.valid"
              :validation-message="validator.fields.name.error"
            />
            <InputComponent :value="secret" :label="labels.secret" readonly :disabled="!isNew">
              <template #below>
                <Alert v-if="isNew" class="mt-1" theme="warning">
                  {{ $t('tokenSecretMessage') }}
                </Alert>
              </template>
            </InputComponent>

            <InputComponent :label="labels.acl">
              <template #default>
                <div class="token-mutate__scroller">
                  <ChecklistComponent v-model="data.acl" :items="acl" class="elder-input__element" />
                </div>
              </template>
            </InputComponent>

            <InputComponent :value="expiresAt" :label="labels.expiresAt" data-field="expiresAt">
              <InputDate v-model="expiresAt" />
            </InputComponent>

            <InputComponent
              :label="labels.user"
              required
              :is-valid="validator.fields.reference.valid"
              :validation-message="validator.fields.reference.error"
            >
              <UserSelect
                v-model="data.reference"
                search
                :items="fetchUsers"
                :title="$t('selectx', { x: labels.user.toLowerCase() })"
                class="elder-input__element"
            /></InputComponent>

            <CheckboxComponent v-if="$store.getters.isSuperadmin" v-model="data.isSystem" :label="labels.isSystem" />
          </div>
        </template>
        <template #footer>
          <Alert class="alert"
            ><CheckboxComponent
              v-model="tokenCompliance"
              :disabled="!isNew"
              required
              :is-valid="validator.fields.tokenCompliance.valid"
              :validation-message="validator.fields.tokenCompliance.error"
              :label="$t('integrationCompliance')"
          /></Alert>

          <ButtonComponent
            :label="isNew ? $t('create') : $t('save')"
            theme="primary"
            type="submit"
            :disabled="!validator.passes"
            :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
            :promise="promises.mutate"
          />

          <DropdownComponent v-if="!isNew" :close-on-click="false">
            <ButtonComponent :icon="{ icon: ['fal', 'ellipsis-v'], size: 'lg' }" />
            <template #dropdown>
              <ButtonComponent :label="$t('delete')" @click="removeDialog = true" :icon="['fal', 'times']" />
            </template>
          </DropdownComponent>
        </template>
      </Card>

      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'settings',
            params: { step: 'tokens' },
          })
        "
        :resource="$tc('token')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import { getSecureRandomString } from '@/utils'
import { DateTransformer } from '@/utils/transformers'
import UserApi from '@/api/User'

import UserSelect from '@/components/User/Select/Modal'
import HeaderComponent from '@/components/Header'
import ChecklistComponent from '@/components/Select/Checklist'
import Card from '@/components/Card'
import RemoveComponent from '@/components/Remove'
import { DropdownComponent } from 'vue-elder-dropdown'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent as Alert } from 'vue-elder-alert'
import InputDate from '@/components/InputDate'

export default {
  mixins: [
    Validator({
      data: 'mergedData',
      labels: 'labels',
      rules: {
        name: 'required|string',
        acl: 'array',
        reference: 'required',
        tokenCompliance: 'accepted|required',
      },
    }),
    Model({
      source: 'item',
      model: {
        id: null,
        name: null,
        acl: [],
        secret: getSecureRandomString(50),
        reference: null,
        expiresAt: null,
        isSystem: false,
      },
      guard: true,
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      tokenComplianceState: null,
    }
  },
  watch: {
    id: {
      handler(id) {
        if (!id) return this.set(null)
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(['enums']),
    ...mapState('Token', ['item', 'promises']),
    ...mapi18n('labels', {
      name: 'name',
      acl: 'acl',
      secret: 'webhookSecret',
      isSystem: 'webhookIsSystem',
      expiresAt: 'validTo',
      user: ['user', 1],
    }),
    tokenCompliance: {
      get: function () {
        return this.isNew ? this.tokenComplianceState : true
      },
      set: function (newValue) {
        return (this.tokenComplianceState = newValue)
      },
    },
    mergedData() {
      return {
        ...this.data,
        tokenCompliance: this.tokenCompliance,
      }
    },
    isNew() {
      return !this.id
    },
    expiresAt: DateTransformer('data.expiresAt'),
    breadcrumbs() {
      return [
        { title: this.$tc('setting', 2), action: { name: 'settings' } },
        { title: this.$tc('token', 2), action: { name: 'settings', params: { step: 'tokens' } } },
        { title: this.$metaInfo.title },
      ]
    },
    acl() {
      return this.enums.ACL.reduce((res, cur) => {
        cur.operations.forEach(operation => {
          res.push({
            value: `${cur.resource}:${operation}`,
            label: `${cur.resource}:${operation}`,
          })
        })
        return res
      }, [])
    },
    secret() {
      if (this.isNew) return this.data.secret
      return new Array(50).fill('*').join('')
    },
  },
  methods: {
    ...mapActions('Token', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Token', ['set']),
    submit() {
      this.mutate([
        {
          id: this.id,
          ...this.getData({
            transform: {
              reference: v => v && { onModel: v.onModel || 'User', ref: v.id },
              secret: v => (!this.isNew ? undefined : v),
            },
          }),
        },
        {
          strategy: (state, { id }) => {
            return { ...this.getData({ transform: false }), id }
          },
        },
      ]).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'settings',
          params: { step: 'tokens' },
        })
      })
    },
    fetchUsers(filter) {
      return UserApi.get({ filter }, { query: 'UserSearch' })
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createToken') : this.$path('item.name'),
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    ChecklistComponent,
    CheckboxComponent,
    RemoveComponent,
    Card,
    Alert,
    UserSelect,
    InputDate,
  },
}
</script>

<style lang="scss">
.token-mutate {
  background-color: $background-grey;
  $limit: 950px;

  .alert {
    width: 100%;
    .elder-checkbox__box {
      border-color: css-darken('border-color', 15%);
    }
  }

  &__form {
    margin: 0 auto;
    max-width: $limit;

    [type='submit'] {
      margin-right: auto;
    }
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    align-items: flex-start;
  }

  &__scroller {
    max-height: 350px;
    overflow-y: scroll;

    @include scrollShadow();
  }
}
</style>
